import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, useLocation } from "react-router-dom";
import { Alert, message } from "antd";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isPublicPath } from "../config/publicPaths";
import { AUTH_RETURN_PATH_STORAGE_KEY } from "../config/authReturnPath";
import LoadingComponent from "../components/LoadingComponent";
import {
  create,
  getQuery,
  purgeLegacyAuthStorage,
  setAuthorization,
  setViewAsRole,
} from "../services/apiService";
import {
  useAddLoginHistoryService,
  type LoginHistoryPayload,
} from "../services/userService";
import {
  reconcileOperatorTwoFactorSignal,
  useOperatorTwoFactorSignal,
} from "../services/operatorTwoFactorSignal";
import getDeviceDetails from "../utils/DeviceDetails";
import {
  isLoginTwoFactorRefusal,
  requestLoginTwoFactorClearance,
  setLoginTwoFactorRunner,
} from "../services/loginTwoFactorGate";
import type { LoginTwoFactorVerified } from "../services/loginTwoFactorClient";
import LoginTwoFactorPage from "../pages/LoginTwoFactor/LoginTwoFactorPage";

interface AuthUser {
  sub?: string;
  email?: string;
}

interface UserProfile {
  _id?: string;
  id?: string;
  email?: string;
  role?: number;
  /**
   * The operator's OWN authenticator enrolment, as /user/me already returns it —
   * `setUserProfile` has always stored the whole payload, only this type narrowed
   * it away. Read by useOperatorTwoFactor to warn before an action the API's
   * requireTwoFactor() would refuse with 400 TWO_FACTOR_NOT_ENABLED (ENG-550).
   */
  twoFactorAuth?: {
    enabled?: boolean;
    requiredForActions?: string[];
  };
}

interface UserBusinessFeatures {
  businessId?: string;
  businessName?: string;
  roleKey: string;
  roleName: string;
  features: Array<{
    key: string;
    name: string;
  }>;
}

interface UserFeatureAccess {
  platform: string[];
  administrator: string[];
  /**
   * The signed-in operator's administrator governance tier (higher = more
   * privileged). The server refuses to grant or define a role above the
   * caller's tier; the console mirrors that ceiling in its role pickers.
   * Absent on older API builds — treat as 0.
   */
  administratorTier?: number;
  /**
   * The administrator roles the operator holds, as `{ key, name }`. The server
   * has always returned this alongside the capability list; the console needs it
   * because a few admin routes are gated on the ROLE and not on a capability, so
   * no feature key can answer "may I press this" (console/lib/adminRoles.ts).
   * Absent on older API builds — treat as none.
   */
  administratorRoles?: Array<{ key: string; name: string }>;
  businesses: UserBusinessFeatures[];
}

/**
 * An active "view-as-role" preview (the super-admin role switcher). While set,
 * hasAdminFeature/adminTier report the PREVIEWED role, and the X-View-As-Role
 * header makes the server authorize as that role too (downgrade-only, read-only).
 */
export interface ViewAsPreview {
  roleKey: string;
  roleName: string;
  tier: number;
  featureKeys: string[];
}

interface AuthContextValue {
  isAuthenticated: boolean;
  login: () => Promise<React.ReactNode | void>;
  logout: () => Promise<void>;
  userProfile: UserProfile | null;
  userFeatures: UserFeatureAccess | null;
  authStatus: "loading" | "ready" | "error";
  hasAdminFeature: (featureKey: string) => boolean;
  /**
   * The EFFECTIVE admin role keys — the previewed role's while previewing, else
   * every administrator role the operator holds. For the handful of controls
   * whose API gate is a role list rather than a capability; read it through the
   * helpers in console/lib/adminRoles.ts rather than comparing strings here.
   */
  adminRoleKeys: string[];
  /** The EFFECTIVE admin tier — the previewed role's while previewing, else the operator's own. */
  adminTier: number;
  /** The operator's REAL admin tier, never affected by a preview — gates the switcher (≥ 90). */
  realAdminTier: number;
  /** The active view-as preview, or null. */
  viewAs: ViewAsPreview | null;
  /** Start previewing a lower role (super-admins only; the server enforces downgrade-only). */
  startViewAs: (roleKey: string) => Promise<void>;
  /** Exit the preview and return to the operator's own identity. */
  stopViewAs: () => Promise<void>;
  /**
   * Re-read the signed-in profile so `userProfile.twoFactorAuth` reflects an
   * enrolment or disable that just happened (ENG-1378). Never throws: a failed
   * re-read leaves the profile as it was, and the server's last word
   * (services/operatorTwoFactorSignal) keeps standing in for it.
   */
  refreshOperatorTwoFactor: () => Promise<void>;
  user?: AuthUser;
}

interface V2Response<T> {
  success: boolean;
  data: T;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * After a recovery-code sign-in: that code is spent, so say how many are left.
 * Draft wording, pending sign-off.
 */
function RecoveryCodesLeftNotice({
  remaining,
  onClose,
}: {
  remaining: number;
  onClose: () => void;
}) {
  const left =
    remaining === 0
      ? "You have no recovery codes left."
      : `You have ${remaining} recovery code${remaining === 1 ? "" : "s"} left.`;
  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1100,
        width: "min(560px, calc(100vw - 32px))",
      }}
    >
      <Alert
        type={remaining <= 2 ? "warning" : "info"}
        showIcon
        closable
        onClose={onClose}
        message={`You signed in with a recovery code. ${left}`}
        description="Each recovery code works once. Make a new set in the security settings of your 80eight account before you run out."
      />
    </div>
  );
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userFeatures, setUserFeatures] = useState<UserFeatureAccess | null>(
    null
  );
  const [authStatus, setAuthStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const [viewAs, setViewAs] = useState<ViewAsPreview | null>(null);
  const queryClient = useQueryClient();
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    logout: auth0Logout,
  } = useAuth0();
  const location = useLocation();
  const isPublicRoute = isPublicPath(location.pathname);
  const { addLoginHistory } = useAddLoginHistoryService();

  // 🔴 SIGN-IN TWO-FACTOR. While a challenge is open, the code screen covers
  // the console; every request refused with 403 LOGIN_TWO_FACTOR_REQUIRED is
  // waiting on it (services/loginTwoFactorGate.ts) and goes again once the code
  // is accepted. The console underneath stays MOUNTED, so a form being filled
  // in when a silent token renewal triggered the screen keeps what was typed.
  const [loginChallengeOpen, setLoginChallengeOpen] = useState(false);
  const loginChallengeRef = useRef<{
    resolve: () => void;
    reject: (reason: Error) => void;
  } | null>(null);
  /** Codes left after a recovery-code sign-in, until the operator dismisses it. */
  const [recoveryCodesLeft, setRecoveryCodesLeft] = useState<number | null>(
    null
  );

  /** Close the challenge: every waiting request goes again, or gives up. */
  const settleLoginChallenge = (outcome: "cleared" | "abandoned") => {
    const challenge = loginChallengeRef.current;
    loginChallengeRef.current = null;
    setLoginChallengeOpen(false);
    if (outcome === "cleared") challenge?.resolve();
    else challenge?.reject(new Error("Sign-in code not entered."));
  };

  // Registered BEFORE the sign-in effect below runs, so the bootstrap can use it.
  useEffect(() => {
    setLoginTwoFactorRunner(
      () =>
        new Promise<void>((resolve, reject) => {
          loginChallengeRef.current = { resolve, reject };
          setLoginChallengeOpen(true);
        })
    );
    return () => {
      setLoginTwoFactorRunner(null);
      loginChallengeRef.current?.reject(new Error("Signed out."));
      loginChallengeRef.current = null;
    };
  }, []);

  const finishLoginChallenge = (result: LoginTwoFactorVerified) => {
    settleLoginChallenge("cleared");
    if (
      result.factor === "recovery_code" &&
      typeof result.recoveryCodesRemaining === "number"
    ) {
      setRecoveryCodesLeft(result.recoveryCodesRemaining);
    }
  };

  const login = async () => {
    setAuthStatus("loading");
    try {
      const audience = process.env.REACT_APP_AUTH_AUDIENCE;
      const tokenResponse = await getAccessTokenSilently({
        authorizationParams: {
          audience, //'https://dev-fu88hpvhk3wk7i2x.us.auth0.com/api/v2/',//*/"https://auth.80eight.io.us.auth0.com/api/v2/",
          scope: "openid profile email",
        },
      });
      const token = typeof tokenResponse === "string" ? tokenResponse : "";

      // The access token is held in memory by the Auth0 SDK (no `cacheLocation`
      // is configured, so it defaults to in-memory) and put on the axios default
      // header here. Nothing about the session is written to web storage — the
      // ID-token claims used to be, including the raw JWT, and nothing ever read
      // them back (T-184).
      setAuthorization(token);

      // v2 response: axios wraps in response.data, giving { success: true, data: UserProfile }
      // A sign-in two-factor refusal on these two is handled below, by
      // re-running this whole sign-in, not by replaying each request.
      const [profileResponse, featureResponse] = await Promise.all([
        getQuery<V2Response<UserProfile>>("/api/v2/user/me", undefined, {
          loginTwoFactor: "caller",
        }),
        getQuery<V2Response<UserFeatureAccess>>(
          "/api/v2/user/me/features?platform=web",
          undefined,
          { loginTwoFactor: "caller" }
        ),
      ]);

      if (profileResponse.status === 200 && profileResponse.data.data) {
        setUserProfile(profileResponse.data.data);
        setUserFeatures(featureResponse.data.data);
        setAuthStatus("ready");

        // 🔴 TELEMETRY, IN ITS OWN try. Everything below this line is
        // login-history recording — it is not part of authenticating, and it
        // runs AFTER `setAuthStatus("ready")`. Sharing the outer catch meant a
        // failure here nulled the profile and features and flipped the status
        // to "error", so a successful login was undone by a failed IP lookup
        // and reported as "Could not fetch user profile".
        //
        // `getDeviceDetails` no longer throws, so this is belt and braces —
        // but the guarantee belongs here too. The next thing added to this
        // block should not be able to log an operator out either.
        try {
          if (!sessionStorage.getItem("loginHistoryRecorded")) {
            // No `await` and no IP: `getDeviceDetails` reads navigator.userAgent
            // and nothing else. It used to call api.ipify.org for the console's
            // own public IP, which the CSP blocks by design — the API now takes
            // the IP off the connection, where it is both correct and beyond the
            // client's reach.
            const payload: LoginHistoryPayload = getDeviceDetails();

            addLoginHistory(
              payload,
              () => {
                sessionStorage.setItem("loginHistoryRecorded", "true");
              },
              (error) => {
                // ONE toast, and it says the thing the operator can act on:
                // their sign-in is not in the audit trail. The detail goes to
                // the browser console for whoever is debugging it, because the
                // server's own wording is an ORM error, not an explanation.
                console.warn("[login-history] failed to record sign-in", error);
                message.error("Failed to record login history");
              }
            );
          }
        } catch {
          // A login the operator completed stands, whatever telemetry did.
        }
      }
    } catch (error) {
      // 🔴 Not a failed sign-in: sign-in two-factor is on for this operator and
      // this bearer has not passed the code yet. Ask for it, then sign in again
      // with the SAME bearer — the answer carries no token and needs none.
      // Treating it as an error would send the operator to /unauthorized.
      if (isLoginTwoFactorRefusal(error)) {
        try {
          await requestLoginTwoFactorClearance();
        } catch {
          return; // They chose to sign out instead.
        }
        return login();
      }
      setUserProfile(null);
      setUserFeatures(null);
      setAuthStatus("error");
      message.error(`Could not fetch user profile.`);
    }
  };

  // Re-read /user/me rather than patching `twoFactorAuth.enabled` from the
  // verify/disable answer: the server is the source of truth for the whole
  // `twoFactorAuth` block (`requiredForActions` moves with it), the same call
  // already runs at sign-in, and a patched flag would hide a verify that the
  // server accepted but recorded differently. One request, on an event that
  // happens once per operator.
  const refreshOperatorTwoFactor = useCallback(async () => {
    try {
      const response =
        await getQuery<V2Response<UserProfile>>("/api/v2/user/me");
      const fresh = response.data?.data;
      if (!fresh) return;
      setUserProfile(fresh);
      reconcileOperatorTwoFactorSignal(fresh.twoFactorAuth?.enabled);
    } catch {
      // Leave the profile as it was; the signal set by the caller still wins.
    }
  }, []);

  // A stale profile: it says "enrolled" but the server just refused a request
  // for want of a factor (reset elsewhere). The gates already follow the server;
  // re-read the profile once so it agrees. Keyed on the pair, so a profile that
  // still disagrees after the re-read is not fetched again in a loop.
  const operatorTwoFactorSignal = useOperatorTwoFactorSignal();
  const profileSaysEnrolled = userProfile?.twoFactorAuth?.enabled === true;
  useEffect(() => {
    if (operatorTwoFactorSignal === "setup-required" && profileSaysEnrolled) {
      void refreshOperatorTwoFactor();
    }
  }, [operatorTwoFactorSignal, profileSaysEnrolled, refreshOperatorTwoFactor]);

  useEffect(() => {
    if (isPublicRoute) return;
    if (isAuthenticated) {
      login();
    }
  }, [isAuthenticated, user, isPublicRoute]);

  useEffect(() => {
    if (isPublicRoute) return;
    if (!isLoading && !isAuthenticated) {
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      sessionStorage.setItem(AUTH_RETURN_PATH_STORAGE_KEY, returnTo);

      void loginWithRedirect({
        appState: {
          returnTo,
        },
        async openUrl(url) {
          const targetUrl = String(url);
          sessionStorage.setItem("authRedirectTarget", targetUrl);
          window.location.replace(targetUrl);
        },
      }).catch(() => {
        message.error("Authentication redirect failed.");
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, isPublicRoute]);

  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Show a loading state while the SDK processes authentication
  if (isLoading || !isAuthenticated) {
    return <LoadingComponent />;
  }

  if (authStatus === "error") {
    return <Navigate to="/unauthorized" replace />;
  }

  const logout = async () => {
    // Clear any active preview FIRST — the sign-out POST must not carry
    // X-View-As-Role, or the server's read-only guard would reject it.
    setViewAsRole();
    setViewAs(null);
    try {
      await create("/api/v2/auth/sign-out");
    } catch (error) {
      console.error("Backend sign out failed:", error);
    } finally {
      sessionStorage.clear();
      // `authId` lives in LOCAL storage, which sessionStorage.clear() does not
      // touch — so without this the console's own sign-out left an ex-session's
      // Auth0 subject in the browser. This is the /console sign-out path
      // (ConsoleApp); `performLogout` is the legacy AdminLayout one. Both purge,
      // or the guarantee only holds on whichever surface you happened to use
      // (T-184).
      purgeLegacyAuthStorage();
      setAuthorization();
      setIsAuthenticated(false);
      setUserProfile(null);
      setUserFeatures(null);
      setAuthStatus("loading");
      auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  const realAdminTier = userFeatures?.administratorTier ?? 0;

  /**
   * Start previewing a lower role. Validates + audits server-side via
   * POST /admin/view-as (which returns the previewed features + tier), then arms the
   * X-View-As-Role header so every subsequent request authorizes as that role, and
   * refetches all data so the console reflects the preview. The header is cleared for
   * the start call itself (the server's read-only guard would otherwise reject the POST).
   */
  const startViewAs = async (roleKey: string) => {
    setViewAsRole(undefined);
    try {
      const res = await create<V2Response<ViewAsPreview>>(
        "/api/v2/admin/view-as",
        { roleKey }
      );
      const preview = res.data.data;
      setViewAsRole(preview.roleKey);
      setViewAs(preview);
      // Refetch all data under the preview — EXCEPT the admin roles list, a
      // super-admin-only endpoint (/admin/user-roles) that 403s for the previewed
      // lower role. Its picker is hidden while previewing anyway.
      await queryClient.invalidateQueries({
        predicate: (q) =>
          !(
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === "access-control" &&
            q.queryKey[1] === "user-roles"
          ),
      });
    } catch (error) {
      // Restore the previous preview header (if any) so a failed switch doesn't
      // silently leave requests un-headered mid-preview.
      setViewAsRole(viewAs?.roleKey);
      message.error(
        error instanceof Error && error.message
          ? error.message
          : "Could not start the role preview."
      );
    }
  };

  /** Exit the preview: drop the header, clear state, and refetch as the real operator. */
  const stopViewAs = async () => {
    setViewAsRole(undefined);
    setViewAs(null);
    await queryClient.invalidateQueries();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        userProfile,
        userFeatures,
        authStatus,
        // While previewing, gate on the PREVIEWED role's features/tier so the whole
        // console UI (nav, buttons, tabs) renders as that role would see it. Not
        // previewing → the operator's own features/tier, exactly as before.
        hasAdminFeature: (featureKey: string) =>
          viewAs
            ? viewAs.featureKeys.includes(featureKey)
            : (userFeatures?.administrator.includes(featureKey) ?? false),
        // Same rule as hasAdminFeature: a preview is a SINGLE role, so it
        // replaces the operator's own list rather than adding to it — otherwise
        // a super-admin previewing admin.support would still be shown the
        // write-admin buttons the preview is meant to hide.
        adminRoleKeys: viewAs
          ? [viewAs.roleKey]
          : (userFeatures?.administratorRoles ?? []).map((role) => role.key),
        adminTier: viewAs ? viewAs.tier : realAdminTier,
        realAdminTier,
        viewAs,
        startViewAs,
        stopViewAs,
        refreshOperatorTwoFactor,
        user,
      }}
    >
      {loginChallengeOpen && (
        <LoginTwoFactorPage
          onVerified={finishLoginChallenge}
          onSignOut={() => {
            settleLoginChallenge("abandoned");
            void logout();
          }}
        />
      )}
      {recoveryCodesLeft !== null && !loginChallengeOpen && (
        <RecoveryCodesLeftNotice
          remaining={recoveryCodesLeft}
          onClose={() => setRecoveryCodesLeft(null)}
        />
      )}
      {/* Hidden, never unmounted, while the code screen is up. `contents`
          keeps this wrapper out of every layout the console builds. */}
      <div style={{ display: loginChallengeOpen ? "none" : "contents" }}>
        {children}
      </div>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
